import { Box, Button, Typography } from '@mui/material';
import { GoogleMap, InfoWindow, MarkerF, MarkerClustererF } from '@react-google-maps/api';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';

import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { URL_CONSTANT } from '../URLConstants';
import { lockIcon } from '../Constants';
import { ThemeContext } from '../App';
import { getLocationFromLatLng } from '../utils/Parser';
import { ReactComponent as BatteryIcon } from '../icons/batteryMapIcon.svg';
import { ReactComponent as LocationIcon } from '../icons/locationMapIcon.svg';
import { ReactComponent as LockStatusIcon } from '../icons/mapLockIcon.svg';

const useStyles = makeStyles({
  textStyle: {
    fontSize: '12px',
    color: '#000000',
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  textStyleLight: {
    fontSize: '12px',
    color: '#848484'
  },
  infoWindow: {
    background: '#FFF',
    borderRadius: '10px',
    padding: '10px',
    minWidth: '200px',
    maxWidth: '270px',
    boxShadow: '0px 10px 19px 1px #EAEAFF'
  },
  infoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  statusDot: {
    height: '10px',
    width: '10px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '5px'
  },
  iconText: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '5px'
  },
  driverInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '5px'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px'
  },
  mapLabel: {
    color: 'rgb(0 0 0 / 70%) !important',
    marginTop: '-38px',
    fontSize: '10px !important',
    background: 'white !important',
    boxShadow: '0 4px 8px rgb(0 0 0 / 20%)',
    padding: '4px',
    whiteSpace: 'pre',
    width: 'auto',
    fontFamily: '"Montserrat", sans-serif',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    borderRadius: '4px'
  },
  mapLabelFirstLine: {
    color: 'rgb(0 0 0 / 85%) !important',
    fontSize: '10px !important',
    fontFamily: '"Montserrat", sans-serif'
  }
});

export default function MapView({ deviceList, alertMetas, setAlertMetas }) {
  const navigate = useNavigate();
  const [dragged, setDragged] = useState(false);
  const [map, setMap] = useState(null);
  const [cordinates, setCordinates] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const isLoaded = useContext(ThemeContext);
  const [center, setCenter] = useState({ lat: 28.499359150742354, lng: 77.06975633821554 });
  const [deviceLocationName, setDeviceLocationName] = useState([]);
  const classes = useStyles();

  const mapStyles = {
    height: '100%',
    width: '100%'
  };

  const options = {
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: false,
    scaleControl: false,
    keyboardShortcuts: false
  };

  const handleCloseInfoWindow = () => {
    setSelectedDevice(null);
  };

  const handleTripPage = (data) => {
    if (data?.tripData) {
      navigate(URL_CONSTANT.TRIP_DETAIL, { state: { tripData: data?.tripData } });
    } else {
      setAlertMetas({
        ...alertMetas,
        severity: 'info',
        snackBarOpened: true,
        msg: 'Device is not assigned to any trip !'
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  useEffect(() => {
    setCordinates(
      deviceList?.filter(
        (item) =>
          item?.latestEvent?.location_lat !== 0 &&
          item?.latestEvent?.location_lng !== 0 &&
          item?.latestEvent !== null
      )
    );
  }, [deviceList]);

  const handleInfoWindow = async (data) => {
    setSelectedDevice(data);
    setDeviceLocationName('Fetching...');

    if (data.latestEvent?.location_lat && data.latestEvent?.location_lng) {
      const fetchedLocation = await getLocationFromLatLng(
        data.latestEvent.location_lat,
        data.latestEvent.location_lng
      );
      setDeviceLocationName(fetchedLocation || 'Location not found');
    } else {
      setDeviceLocationName('Invalid coordinates');
    }
  };

  useEffect(() => {
    if (!map || !cordinates) return;
    const bounds = computeBounds(window.google.maps, cordinates);
    if (bounds === null) return;
    if (!dragged) {
      map.fitBounds(bounds, 70);
      setCenter(bounds.getCenter());
    }
    setCordinates(cordinates);
  }, [cordinates, map, dragged]);

  useEffect(() => {
    if (map && cordinates?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      cordinates.forEach((item) => {
        if (item?.latestEvent?.location_lat && item?.latestEvent?.location_lng) {
          bounds.extend({
            lat: item?.latestEvent?.location_lat,
            lng: item?.latestEvent?.location_lng
          });
        }
      });
      map.fitBounds(bounds);
    }
  }, [map, cordinates]);

  const onMapLoad = useCallback((map) => setMap(map), []);

  return isLoaded ? (
    <Box data-testid="map-view" sx={{ height: '100%', width: '100%' }}>
      <GoogleMap
        mapContainerStyle={mapStyles}
        onDrag={() => setDragged(true)}
        center={center}
        zoom={15}
        onLoad={onMapLoad}
        options={options}
      >
        <MarkerClustererF>
          {(clusterer) =>
            cordinates?.map((item, index) => (
              <MarkerF
                key={item?.id}
                position={{
                  lat: item?.latestEvent?.location_lat,
                  lng: item?.latestEvent?.location_lng
                }}
                icon={{
                  url: getIcon(item?.deviceLiveStatus)
                }}
                label={{
                  text: `${item?.name}`,
                  // text: `${item?.name}\n${deviceLocationName[index]}`,
                  className: classes.mapLabel
                }}
                clusterer={clusterer}
                onClick={() => handleInfoWindow(item)}
              >
                {selectedDevice?.id === item.id && (
                  <InfoWindow onCloseClick={handleCloseInfoWindow}>
                    <div className={classes.infoWindow}>
                      <div className={classes.infoHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {LockStatusIcon ? <LockStatusIcon /> : null}
                          <Typography style={{ margin: '0', color: '#6F6F6F' }}>
                            {item?.name}
                          </Typography>
                        </div>
                        <SignalCellularAltIcon
                          sx={{
                            color:
                              item?.deviceLiveStatus?.toUpperCase() === 'OFFLINE'
                                ? '#C30A0A'
                                : '#0DA626'
                          }}
                        />
                      </div>
                      <div className={classes.iconText}>
                        {BatteryIcon ? <BatteryIcon /> : null}
                        <Typography className={classes.textStyleLight}>
                          {item?.latestEvent?.battery_remaining}%
                        </Typography>
                      </div>
                      <div className={classes.iconText}>
                        {LocationIcon ? <LocationIcon /> : null}
                        <Typography className={classes.textStyleLight}>
                          {deviceLocationName}
                        </Typography>
                      </div>

                      <div
                        className={classes.driverInfo}
                        style={{ borderTop: '1px solid #B0B9C7', paddingTop: '10px' }}
                      >
                        <Typography className={classes.textStyle}>Driver</Typography>
                        <Typography className={classes.textStyle}>
                          {item?.tripData?.driver_name}
                        </Typography>
                      </div>
                      <div className={classes.driverInfo}>
                        <Typography className={classes.textStyle}>Phone</Typography>
                        <Typography className={classes.textStyle}>
                          {item?.tripData?.driver_phone}
                        </Typography>
                      </div>
                      <div className={classes.driverInfo}>
                        <Typography className={classes.textStyle}>Vehicle</Typography>
                        <Typography className={classes.textStyle}>
                          {item?.vehicledata?.name}
                        </Typography>
                      </div>
                      <div className={classes.driverInfo}>
                        <Typography className={classes.textStyle}>Device IMEI</Typography>
                        <Typography className={classes.textStyle}>
                          {item?.latestEvent?.device_id}
                        </Typography>
                      </div>
                      <Box className={classes.buttonsContainer}>
                        <Button variant="outlined" onClick={() => handleTripPage(item)} fullWidth>
                          Trip page
                        </Button>
                      </Box>
                    </div>
                  </InfoWindow>
                )}
              </MarkerF>
            ))
          }
        </MarkerClustererF>
      </GoogleMap>
    </Box>
  ) : null;
}

const computeBounds = (map, cordinates) =>
  !map || cordinates?.length === 0
    ? null
    : cordinates?.reduce(
        (bounds, device) =>
          bounds.union(
            new map.LatLngBounds({
              lat: device?.latestEvent?.location_lat,
              lng: device?.latestEvent?.location_lng
            })
          ),
        new map.LatLngBounds({
          lat: cordinates[0]?.latestEvent?.location_lat,
          lng: cordinates[0]?.latestEvent?.location_lng
        })
      );

function getIcon(status) {
  switch (status) {
    case 'online':
      return onDuty;
    case 'assigned':
      return onDuty;
    case 'unassigned':
      return awaitingDuty;
    case 'offline':
      return offline;
    case 'error':
      return error;
    default:
      return defaultIcon;
  }
}

const onDuty = lockIcon('#027148');
const awaitingDuty = lockIcon('#FF5733');
const offline = lockIcon('#8B0000');
const error = lockIcon('#FFC300');
const defaultIcon = lockIcon('#171D40');
